import {
    Component,
    ElementRef,
    HostBinding,
    OnInit,
    Renderer2,
    inject,
    output,
    viewChild,
    viewChildren,
} from '@angular/core';
import { DepositFormService } from '../deposit-form.service';
import { MatStepper, MatStep, MatStepLabel } from '@angular/material/stepper';
import { TabComponentModel } from '../../shared/interfaces/tab-component.model';
import { DepositFormDataService } from '../deposit-form-data.service';
import { SnackbarService } from '../../core/snackbar.service';
import {
    GrowInAnimation,
    GrowOutAnimation,
    OpacityInAnimation,
    OpacityOutAnimation,
} from '@exl-ng/mulo-common';
import { SuccessSnackbarComponent } from '../../parts/snackbars/success-snackbar/success-snackbar.component';
import { ProfileOutputService } from '../../profile/profile-output/profile-output.service';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { RouterService } from '../../core/router.service';
import { DepositForm } from '../../shared/configurations/deposit-form.constant';
import { ResearcherOutputListFacet } from '../../shared/interfaces/researcher-output-list-facet.interface';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { TranslateModule } from '@ngx-translate/core';
import { DepositStepperNextButtonComponent } from './deposit-stepper-next-button/deposit-stepper-next-button.component';
import { DepositStepperSkipToEndButtonComponent } from './deposit-stepper-skip-to-end-button/deposit-stepper-skip-to-end-button.component';
import { DepositLastStepComponent } from '../deposit-steps/deposit-last-step/deposit-last-step.component';
import { DepositStepFourComponent } from '../deposit-steps/deposit-step-four/deposit-step-four.component';
import { DepositStepThreeComponent } from '../deposit-steps/deposit-step-three/deposit-step-three.component';
import { DepositStepTwoComponent } from '../deposit-steps/deposit-step-two/deposit-step-two.component';
import { DepositStepOneComponent } from '../deposit-steps/deposit-step-one/deposit-step-one.component';
import { DepositStepperBackButtonComponent } from './deposit-stepper-back-button/deposit-stepper-back-button.component';
import { DepositFormOverlayService } from '../deposit-form-overlay/deposit-form-overlay.service';

@Component({
    selector: 'esp-deposit-stepper',
    templateUrl: './deposit-stepper.component.html',
    styleUrls: ['./deposit-stepper.component.scss'],
    animations: [
        OpacityInAnimation,
        OpacityOutAnimation,
        GrowInAnimation,
        GrowOutAnimation,
    ],
    imports: [
        DepositStepperBackButtonComponent,
        MatStepper,
        MatStep,
        MatStepLabel,
        DepositStepOneComponent,
        DepositStepTwoComponent,
        DepositStepThreeComponent,
        DepositStepFourComponent,
        DepositLastStepComponent,
        DepositStepperSkipToEndButtonComponent,
        DepositStepperNextButtonComponent,
        TranslateModule,
    ],
})
export class DepositStepperComponent implements OnInit {
    private depositFormService = inject(DepositFormService);
    private overlaySvc = inject(DepositFormOverlayService);
    depositFormDataService = inject(DepositFormDataService);
    private snackbarService = inject(SnackbarService);
    profileOutputService = inject(ProfileOutputService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private renderer = inject(Renderer2);
    private element = inject<ElementRef<HTMLElement>>(ElementRef);
    private routerService = inject(RouterService);

    readonly closeDeposit = output<any>();
    readonly stepper = viewChild<MatStepper>('stepper');
    @HostBinding('class.header-is-hidden') headerIsHidden = false;
    @HostBinding('class.stepper-active') stepperActive = false;

    readonly stepsContent = viewChildren('step', { read: ElementRef });
    private elementsToHideInLastStep: [];

    visibleSteps: number;
    firstTabComponents: TabComponentModel[] = [];
    secondTabComponents: TabComponentModel[] = [];
    thirdTabComponents: TabComponentModel[] = [];
    fourthTabComponents: TabComponentModel[] = [];
    isLinear = true;
    backButtonDisabled = true;
    lastStep;
    showSkipToEnd = false;
    nextStepEnabled = false;
    processingNextStep = false;
    stepperStepsActive = false;
    jumpToTitle = false;
    jumpToTopics = false;
    jumpToFiles = false;

    public hasBaseDropZoneOver = false;
    public hasAnotherDropZoneOver = false;
    steps = [
        { pos: 0, title: 'research.toptab.tab1', icon: 'file' },
        { pos: 1, title: 'research.toptab.tab2', icon: 'home' },
        { pos: 2, title: 'research.toptab.tab3', icon: 'sort' },
        { pos: 3, title: 'research.toptab.tab4' },
        { pos: 4, title: 'research.top.step4bottom' },
        { pos: 5, title: 'research.top.step5' },
    ];

    ngOnInit() {
        if (!this.depositFormDataService.isDraft()) {
            this.firstTabComponents = [
                new TabComponentModel(
                    'file_uploader',
                    false,
                    true,
                    '1',
                    '',
                    '',
                ),
            ];
        }
        this.visibleSteps = this.steps.length - 3;
    }

    buildTabsPerConfiguration() {
        this.firstTabComponents = JSON.parse(
            JSON.stringify(this.buildTab('tab1')),
        );
        this.secondTabComponents = JSON.parse(
            JSON.stringify(this.buildTab('tab2')),
        );
        this.thirdTabComponents = JSON.parse(
            JSON.stringify(this.buildTab('tab3')),
        );
        this.fourthTabComponents = JSON.parse(
            JSON.stringify(this.buildTab('tab4')),
        );
    }

    buildTab(tab) {
        const resourceTypeTemplate =
            this.depositFormDataService.resourceTypeTemplate;
        const comp =
            this.configurationHandlerService.depositConfig()?.deposit[
                resourceTypeTemplate
            ].tabs[tab].components;
        comp.sort((a, b) => {
            return parseInt(a.order) - parseInt(b.order);
        });
        return comp;
    }

    public fileOverBase(e: any): void {
        this.hasBaseDropZoneOver = e;
    }

    public fileOverAnother(e: any): void {
        this.hasAnotherDropZoneOver = e;
    }

    onBackButtonClick() {
        this.stepper().selectedIndex = this.lastStep;
        this.headerIsHidden = false;
        this.backButtonDisabled = true;
        this.showSkipToEnd = true;

        this.unHideTabsFromAssistiveTechnologies();
    }

    onSkipToEndButtonClick() {
        this.resetJump();
        this.goToSummary();
    }

    get isLastStep() {
        if (this.stepper().selectedIndex >= this.visibleSteps) {
            return true;
        }
        return false;
    }

    goToSummary() {
        this.lastStep = this.stepper().selectedIndex;
        this.stepper().selectedIndex = this.visibleSteps + 1;
        this.headerIsHidden = true;
        this.backButtonDisabled = false;
        this.showSkipToEnd = false;

        this.hideHiddenTabsFromAssistiveTechnologies();
    }

    onNextButtonClick() {
        this.resetJump();
        const stepper = this.stepper();
        if (stepper.selectedIndex === this.visibleSteps) {
            this.goToSummary();
        } else if (stepper.selectedIndex > this.visibleSteps) {
            this.submit();
        } else {
            stepper.selectedIndex = stepper.selectedIndex + 1;
        }
    }

    submit() {
        this.processingNextStep = true;
        this.submitNewAsset();
    }

    submitNewAsset() {
        const depositData = this.depositFormService.buildDepositData();
        this.depositFormService.submitNewAsset(depositData.value).subscribe(
            () => {
                if (this.routerService.isProfileRoute()) {
                    this.refreshDataInOutputListAfterSave();
                }
            },
            (error) => {},
        );
        this.success();
    }

    refreshDataInOutputListAfterSave() {
        setTimeout(() => {
            this.profileOutputService
                .getOutputListFacets(this.profileOutputService.isPublicPage())
                .subscribe((data) => {
                    const isListEmpty =
                        this.profileOutputService.facets.length === 0;
                    const facets = data as ResearcherOutputListFacet[];
                    if (facets && facets.length > 0) {
                        this.profileOutputService.saveFacets(data);
                        if (isListEmpty) {
                            //if the is empty- need to navigate to the first facet
                            this.profileOutputService.refreshData$.next(true);
                        } else {
                            this.profileOutputService.refreshData$.next(false);
                        }
                    }
                });
        }, this.profileOutputService.UPDATE_DATA_TIMEOUT);
    }

    stepChanged(event: StepperSelectionEvent) {
        // Prevent from mat-stepper mark input fields as invalid when step changed.
        if (event.previouslySelectedIndex > event.selectedIndex) {
            event.previouslySelectedStep.interacted = false;
        } else {
            event.selectedStep.interacted = false;
        }
    }

    success() {
        //this.processingNextStep = true;
        setTimeout(() => {
            this.overlaySvc.close();
            this.showSuccessMessage();
        }, 1000);
    }

    showSuccessMessage() {
        setTimeout(() => {
            this.depositFormDataService.resetForm();
            this.depositFormService.updateAmazonInfo();
            this.snackbarService.showComponent(
                SuccessSnackbarComponent,
                'research.deposit.success',
                5000,
            );
        }, 500);
    }

    //TODO change according to configuration in the future
    assetTypeSelected() {
        this.resetJump();
        this.depositFormDataService.cleanForm();
        this.isLinear = false;
        this.nextStepEnabled = true;
        this.showSkipToEnd = true;
        this.buildTabsPerConfiguration();
        this.enableNextStep();
        if (
            !this.depositFormDataService.isDraft() &&
            this.depositFormService.needToSaveDraftInOpen()
        ) {
            this.saveDraftInOpenning();
        }

        this.setElementsToHideInLastStep();
    }

    saveDraftInOpenning() {
        this.depositFormService.needToSaveDraftInOpen.set(false);
        this.depositFormService.disableCloseDialogDuringFirstSave.set(true);
        this.depositFormService.draftInProcess.set(true);
        const depositData = this.depositFormService.buildDepositData(false);
        this.depositFormService.saveDraft(depositData.value, true).subscribe(
            (data) => {
                this.depositFormDataService.updateMmsId(data);
                this.turnOffFlags();
            },
            () => {
                this.turnOffFlags();
            },
        );
    }

    turnOffFlags() {
        this.depositFormService.draftInProcess.set(false);
        this.depositFormService.disableCloseDialogDuringFirstSave.set(false);
    }

    assetTypeCleared() {
        this.resetJump();
        this.nextStepEnabled = false;
        this.showSkipToEnd = false;
        this.stepperStepsActive = false;
        this.stepperActive = false;
        this.isLinear = true;
        this.firstTabComponents = [
            new TabComponentModel('file_uploader', false, true, '1', '', ''),
        ];
        this.secondTabComponents = [];
        this.thirdTabComponents = [];
        this.fourthTabComponents = [];
    }

    displayTab(tab: TabComponentModel[]) {
        if (tab.length === 0) {
            return false;
        }
        const visibleComp = tab.filter((comp) => comp.visible);
        if (visibleComp.length > 0) {
            return true;
        }
        return false;
    }

    enableNextStep() {
        setTimeout(() => {
            this.nextStepEnabled = true;
            this.exposeStepperSteps();
        }, 0);
    }

    exposeStepperSteps() {
        setTimeout(() => {
            this.stepperStepsActive = true;
        }, 0);
        setTimeout(() => {
            this.stepperActive = true;
        }, 300);
    }

    jumpToSection(sectionName) {
        this.headerIsHidden = false;
        this.backButtonDisabled = true;
        this.showSkipToEnd = true;
        if (sectionName === 'title') {
            this.stepper().selectedIndex = 1;
            this.jumpToTitle = true;
        } else if (sectionName === 'topics') {
            this.stepper().selectedIndex = 1;
            this.jumpToTopics = true;
        } else if (sectionName === 'files') {
            this.stepper().selectedIndex = 0;
            this.jumpToFiles = true;
        }

        this.unHideTabsFromAssistiveTechnologies();
    }

    resetJump() {
        this.jumpToTitle = false;
        this.jumpToTopics = false;
        this.jumpToFiles = false;
    }

    /**
     * ! Accessibility
     * Hide
     */
    setElementsToHideInLastStep() {
        setTimeout(() => {
            const headerTabs = [].slice.call(
                this.element.nativeElement.querySelectorAll(
                    'mat-step-header[role=tab]',
                ),
            );
            const stepsContent = this.stepsContent().map(
                (step) => step.nativeElement,
            );
            this.elementsToHideInLastStep = headerTabs.concat(stepsContent);
        }, 1000);
    }

    hideHiddenTabsFromAssistiveTechnologies() {
        this.elementsToHideInLastStep.forEach((step) => {
            this.renderer.setAttribute(step, 'aria-hidden', 'true');
        });
    }

    unHideTabsFromAssistiveTechnologies() {
        this.elementsToHideInLastStep.forEach((step) => {
            this.renderer.removeAttribute(step, 'aria-hidden');
        });
    }
}
